import { useEffect, useMemo, useRef, useState } from "react";

import { Progress } from "@/components/ui/progress";

import { Check, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { ANALYSIS_STEPS, ROTATING_MICROCOPY } from "../../constants";
import Text from "@/components/Text";
import { useTranslation } from "react-i18next";

type StepStatus = "pending" | "loading" | "done";

interface Props {
  onComplete: () => void; /** Total target duration in ms. Default 7500ms feels premium and credible. */
  durationMs?: number;
}

export default function OnboardingAddRestaurantsStep2({
  onComplete,
  durationMs = 7500,
}: Props) {
  const { t } = useTranslation();
  const stepCount = ANALYSIS_STEPS.length;
  const stepDuration = durationMs / stepCount;

  const [progress, setProgress] = useState(0);
  const [activeIndex, setActiveIndex] = useState(0);
  const [microcopyIndex, setMicrocopyIndex] = useState(0); // Drive progress + active step.

  useEffect(() => {
    const startedAt = performance.now();
    let raf = 0;

    const tick = (now: number) => {
      const elapsed = now - startedAt;
      const ratio = Math.min(1, elapsed / durationMs);
      setProgress(Math.round(ratio * 100));

      const idx = Math.min(stepCount - 1, Math.floor(elapsed / stepDuration));
      setActiveIndex(idx);

      if (ratio < 1) {
        raf = requestAnimationFrame(tick);
      } else {
        setActiveIndex(stepCount); // all done
        // tiny pause for the success feel before transition
        setTimeout(onComplete, 450);
      }
    };

    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [durationMs, stepCount, stepDuration, onComplete]); // Rotating microcopy.

  useEffect(() => {
    const interval = setInterval(() => {
      setMicrocopyIndex((i) => (i + 1) % ROTATING_MICROCOPY.length);
    }, 1600);
    return () => clearInterval(interval);
  }, []);

  const stepStatuses: StepStatus[] = useMemo(
    () =>
      ANALYSIS_STEPS.map((_, i) => {
        if (i < activeIndex) return "done";
        if (i === activeIndex) return "loading";
        return "pending";
      }),
    [activeIndex],
  ); // Auto-scroll the steps container so the active step always stays visible.
  // The active item is centered in the scroll viewport for a premium, focused feel.

  const scrollRef = useRef<HTMLUListElement>(null);
  const itemRefs = useRef<Array<HTMLLIElement | null>>([]);

  useEffect(() => {
    const container = scrollRef.current;
    const idx = Math.min(activeIndex, ANALYSIS_STEPS.length - 1);
    const target = itemRefs.current[idx];
    if (!container || !target) return;

    const containerHeight = container.clientHeight;
    const targetTop = target.offsetTop;
    const targetHeight = target.offsetHeight; // Center the active item in the visible scroll area.
    const desiredScrollTop = targetTop - containerHeight / 2 + targetHeight / 2;
    container.scrollTo({
      top: Math.max(0, desiredScrollTop),
      behavior: "smooth",
    });
  }, [activeIndex]);

  return (
    <>
                     {/* Header */}
              
      <div className="space-y-2">
                  
        <div className="bg-[#F4F4F4] py-[4px] px-[8px] rounded-[40px] w-[fit-content]">
          <Text fontSize={14} fontWeight={500} color="#393939">
            {t("step_progress", { step: 2, end: 3 })}
          </Text>
        </div>
                  
        <div className="flex flex-col gap-[10px]">
          <Text fontSize={28} fontWeight={700}>
             
            {t(
              `onboarding_add_restaurants_step_2_label_${ANALYSIS_STEPS[activeIndex]?.label}`,
            )}
          </Text>
          <Text fontSize={14} fontWeight={400} color="#393939">
            {t("onboarding_add_restaurants_step_2_description")}
          </Text>
        </div>
                
      </div>
              {/* Progress */}
              
      <div className="space-y-2 mt-[24px]">
                  
        <div className="flex items-center justify-between text-xs text-muted-foreground tabular-nums">
          <Text fontSize={14} fontWeight={400} color="#161616">
            {t("onboarding_add_restaurants_step_2_1")}
          </Text>
                                  
          <span className="font-medium text-foreground">{progress}%</span>
                    
        </div>
                  
        <Progress color="#0F62FE" value={progress} className="h-2" />
                
      </div>
              
      {/* Steps list — internal scrollable container with auto-follow on active step */}
              
      <div className="relative">
                  
        <ul
          ref={scrollRef}
          className="space-y-1.5 max-h-[clamp(180px,32vh,360px)] overflow-y-auto pr-1 scroll-smooth"
        >
                      
          {ANALYSIS_STEPS.map((step, i) => {
            const status = stepStatuses[i];
            return (
              <li
                key={step.id}
                // @ts-ignore
                ref={(el) => (itemRefs.current[i] = el)}
                className={cn(
                  "flex items-center gap-3 rounded-lg px-3 py-2.5 border transition-all",
                  status === "loading" &&
                    "border-[#0F62FE] bg-[#EDF5FF] shadow-sm",
                  status === "done" &&
                    "border-transparent bg-muted/30 opacity-90",
                  status === "pending" && "border-transparent opacity-50",
                )}
              >
                                  
                <div
                  className={cn(
                    "flex h-6 w-6 shrink-0 items-center justify-center rounded-full border transition-colors",
                    status === "done" &&
                      "bg-[#0F62FE] border-transparent text-primary-foreground",
                    status === "loading" && "border-primary text-primary",
                    status === "pending" &&
                      "border-border text-muted-foreground",
                  )}
                >
                                      
                  {status === "done" && (
                    <Check className="h-3.5 w-3.5" strokeWidth={3} />
                  )}
                                      
                  {status === "loading" && (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  )}
                                      
                  {status === "pending" && (
                    <span className="h-1.5 w-1.5 rounded-full bg-current" />
                  )}
                                    
                </div>
                                  
                <span
                  className={cn(
                    "text-sm",
                    status === "loading" && "font-medium text-[#393939]",
                    status === "done" && "text-[#393939]",
                    status === "pending" && "text-muted-foreground",
                  )}
                >
                                      
                  {t(`onboarding_add_restaurants_step_2_label_${step.label}`)}
                                    
                </span>
                                
              </li>
            );
          })}
                    
        </ul>
                  {/* Subtle fade masks to make overflow feel intentional */}
                  
        <div className="pointer-events-none absolute inset-x-0 top-0 h-6 bg-gradient-to-b from-card to-transparent rounded-t-lg" />
                  
        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-6 bg-gradient-to-t from-card to-transparent rounded-b-lg" />
                
      </div>
              {/* Rotating microcopy */}
              
      <div className="rounded-lg border border-border bg-muted/30 px-3 py-2.5 min-h-[44px] flex items-center">
                  
        <p
          key={microcopyIndex}
          className="text-xs text-muted-foreground animate-fade-in"
        >
                     
          {t(`onboarding_add_restaurants_step_2_micro_label_${microcopyIndex}`)}
                    
        </p>
                
      </div>
              
      <p className="text-[11px] text-center text-muted-foreground mt-[24px]">
                           {t("onboarding_add_restaurants_step_2_3")}
      </p>
                 
    </>
  );
}
